import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PickersActionBarProps } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DialogActions, Stack, Button, useTheme, Box } from "@mui/material";
import { createState } from "@/global/store/zustand_store";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { frFR, enUS } from "@mui/x-date-pickers/locales";
import "dayjs/locale/fr";

dayjs.extend(customParseFormat);

const formatFormValue = (value: any, format: string) => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format(format) : "";
};

export const calcPickerValue = (value: string, format: string) => {
  if (!value) return null;
  const parsed = dayjs(value, format, true);
  return parsed.isValid() ? parsed : null;
};

export const useDeliveryMethodDatePicker = createState<{
  deliveryDate: string | null;
  isError: boolean;
}>({
  deliveryDate: null,
  isError: false,
});

export function DeliveryMethodDatePicker() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const [open, setOpen] = useState(false);
  const { isError, deliveryDate, set } = useDeliveryMethodDatePicker();
  const pickerValue = calcPickerValue(deliveryDate || "", "MM-DD-YYYY");
  const pickerValueFormat =
    i18n.language === "fr" ? "DD MMMM YYYY" : "dddd, MMM DD, YYYY";

  const setValue = (value: any) => {
    const formatted = formatFormValue(value, "MM-DD-YYYY");
    set({ deliveryDate: formatted, isError: false });
  };

  return (
    <>
      <LocalizationProvider
        dateAdapter={AdapterDayjs}
        adapterLocale={i18n.language}
        localeText={{
          ...((i18n.language === "fr" ? frFR : enUS) as any),
          datePickerToolbarTitle:
            t(
              "giftCard.checkout.deliveryMethodSelector.datePickerToolbarTitle"
            ) || "Select Date",
        }}
      >
        <DatePicker
          closeOnSelect
          disablePast
          // @ts-expect-error - mui x date pickers version incompatibility
          enableAccessibleFieldDOMStructure={false}
          desktopModeMediaQuery="@media (min-width: 0px) and (max-width: 0px)"
          format={"dddd, MMM DD, YYYY"}
          value={pickerValue}
          minDate={dayjs(new Date()).add(1, "day")}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          onChange={(value) => setValue(value)}
          sx={{
            "& *": { userSelect: "none", cursor: "pointer" },
            "& svg": { color: theme.palette.grey[700] },
            "& input": {
              caretColor: "transparent",
              "&::selection": { background: "transparent" },
            },
          }}
          slots={{ actionBar: ContainedActionBar }}
          slotProps={{
            actionBar: {
              actions: ["cancel", "accept"],
              onAccept: () => setOpen(false),
              onCancel: () => setOpen(false),
            } as PickersActionBarProps,
            textField: {
              fullWidth: true,
              size: "medium",
              onClick: () => setOpen(true),
              inputProps: {
                readOnly: true,
                placeholder:
                  t(
                    "giftCard.checkout.deliveryMethodSelector.selectDatePlaceholder"
                  ) || "Select Date",
                value:
                  pickerValue
                    ?.locale(i18n.language)
                    .format(pickerValueFormat) || "",
                style: { caretColor: "transparent" },
                contentEditable: false,
              },
            },
            toolbar: {
              toolbarPlaceholder: "",
              sx: {
                "& .MuiPickersToolbar-title": {
                  fontWeight: "bold",
                  fontSize: "1.25rem",
                  color: theme.palette.grey[800],
                  textTransform: "capitalize",
                },
              },
            },
            day: {
              sx: {
                fontSize: "0.9rem",
                borderRadius: "6px",
                "&.MuiPickersDay-today": {
                  border: "none",
                },
              },
            },
          }}
        />
      </LocalizationProvider>

      {isError ? (
        <Box
          className="error-message"
          fontSize={"0.8rem"}
          lineHeight={1.4}
          color={theme.palette.error.main}
          fontWeight={500}
          mt={1}
        >
          {t(
            "giftCard.checkout.deliveryMethodSelector.selectValidDateRequiredText"
          ) || "Please select a valid delivery date."}
        </Box>
      ) : null}
    </>
  );
}

function ContainedActionBar(props: any) {
  const { onAccept, onCancel, actions, ...other } = props;
  const { t } = useTranslation();

  const buttonsMap: Record<string, React.ReactNode> = {
    cancel: (
      <Button variant="outlined" key="cancel" onClick={onCancel}>
        {t("giftCard.checkout.deliveryMethodSelector.closeButtonText") ||
          "Close"}
      </Button>
    ),
    accept: (
      <Button key="accept" variant="contained" onClick={onAccept}>
        {t("giftCard.checkout.deliveryMethodSelector.confirmButtonText") ||
          "Confirm"}
      </Button>
    ),
  };

  return (
    <DialogActions {...other}>
      <Stack p={1} direction="row" spacing={1}>
        {actions?.map((action: string) => buttonsMap[action]).filter(Boolean)}
      </Stack>
    </DialogActions>
  );
}
